import { notification, Segmented } from "antd";
import { get, isEmpty, isNil, noop } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAdd, MdOutlineDescription } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import EmptyState from "shared/components/EmptyState";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Modal from "shared/components/Modal";
import Spin from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { DOCUMENT_KINDS, SCREENS, TYPES } from "utils/constants";
import { API_ERRORS, apiErrorCode, apiErrorDetail } from "../api-errors";
import StaticDocuments from "./Static";
import {
  EMPTY_FILTERS,
  filterTemplates,
  productOptions,
} from "./document-filters";
import FiltersPopover from "./filters-popover";
import NewDocumentModal from "./new-document-modal";
import View, { ROW_ACTIONS } from "./View";

const TEMPLATES_LIMIT = 500;
const PRODUCTS_DETAIL = "products";

const CLOSED_DIALOG = { open: false, template: null, products: [] };

const closeDialog = (dialog) => ({ ...dialog, open: false });

const List = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const LIST = t("LIST", { returnObjects: true });
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { state } = useLocation();
  const [kind, setKind] = useState(state?.kind || DOCUMENT_KINDS.DYNAMIC);
  const [creating, setCreating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(CLOSED_DIALOG);
  const [blockedDialog, setBlockedDialog] = useState(CLOSED_DIALOG);

  const { data, loading, refetch } = useQuery(
    graphql.queries.DOCUMENT_TEMPLATES,
    {
      variables: { skip: 0, take: TEMPLATES_LIMIT },
      fetchPolicy: "network-only",
    },
  );

  const templates = useMemo(
    () => get(data, "documentTemplates.data") || [],
    [data],
  );
  const products = useMemo(() => productOptions(templates), [templates]);
  const visible = useMemo(
    () => filterTemplates(templates, { search, filters }),
    [templates, search, filters],
  );

  const [duplicateTemplate, { loading: duplicating }] = useMutation(
    graphql.mutations.DUPLICATE_DOCUMENT_TEMPLATE,
    { onError: noop },
  );
  const [deleteTemplate, { loading: deleting }] = useMutation(
    graphql.mutations.DELETE_DOCUMENT_TEMPLATE,
    {
      onError: (error) => {
        if (apiErrorCode(error) === API_ERRORS.CONFLICT) {
          setBlockedDialog({
            open: true,
            products: apiErrorDetail(error, PRODUCTS_DETAIL) || [],
          });
          return;
        }
        notification.error({ message: LIST.DELETE.FAILED });
      },
    },
  );
  const [fetchFile] = useLazyQuery(graphql.queries.DOCUMENT_TEMPLATE_FILE, {
    fetchPolicy: "network-only",
    onError: noop,
  });

  const open = ({ id }) =>
    navigate(`${SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.EDIT.path}/${id}`);

  const duplicate = async (template) => {
    if (duplicating) return;
    const { data: duplicated } = await duplicateTemplate({
      variables: {
        where: { id: template.id },
        data: { name: t("LIST.DUPLICATE.NAME", { name: template.name }) },
      },
    });
    if (isNil(get(duplicated, "duplicateDocumentTemplate.id"))) {
      notification.error({ message: LIST.DUPLICATE.FAILED });
      return;
    }
    notification.success({ message: LIST.DUPLICATE.DONE });
    refetch();
  };

  const fileUrl = (template) =>
    fetchFile({ variables: { where: { id: template.id } } })
      .then((result) => get(result, "data.documentTemplateFile"))
      .catch(() => null);

  const download = async (template) => {
    const url = await fileUrl(template);
    if (isEmpty(url)) {
      notification.error({ message: LIST.DOWNLOAD_FAILED });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const askDelete = (template) => setDeleteDialog({ open: true, template });

  const confirmDelete = async () => {
    if (!deleteDialog.open || deleting) return;
    const { data: deleted } = await deleteTemplate({
      variables: { where: { id: deleteDialog.template.id } },
    });
    setDeleteDialog(closeDialog);
    if (isNil(get(deleted, "deleteDocumentTemplate.id"))) return;
    notification.success({ message: LIST.DELETE.DONE });
    refetch();
  };

  const actions = {
    [ROW_ACTIONS.DUPLICATE]: duplicate,
    [ROW_ACTIONS.DOWNLOAD]: download,
    [ROW_ACTIONS.DELETE]: askDelete,
  };

  const onAction = (key, template) => actions[key]?.(template);

  const newDocument = () =>
    kind === DOCUMENT_KINDS.STATIC
      ? navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.CREATE.path, {
          state: { kind: DOCUMENT_KINDS.STATIC },
        })
      : setCreating(true);

  const dynamicContent = () => {
    if (isNil(data)) return loading ? <Spin /> : null;
    if (isEmpty(templates))
      return (
        <div className="dynamic-documents__empty">
          <EmptyState title={LIST.EMPTY.TITLE} icon={MdOutlineDescription} />
          <Button type="primary" onClick={newDocument}>
            <MdAdd size={20} />
            <span>{LIST.HEADER.CREATE}</span>
          </Button>
        </div>
      );
    return (
      <>
        <div className="dynamic-documents__toolbar">
          <div className="dynamic-documents__search">
            <Input
              type="search"
              placeholder={LIST.SEARCH}
              value={search}
              allowClear
              onChange={({ target: { value } }) => setSearch(value)}
            />
          </div>
          <FiltersPopover
            filters={filters}
            products={products}
            onChange={setFilters}
          />
        </div>
        <View data={visible} onOpen={open} onAction={onAction} />
      </>
    );
  };

  return (
    <>
      <Header
        title={LIST.HEADER.TITLE}
        actions={[
          {
            text: LIST.HEADER.CREATE,
            onClick: newDocument,
            type: "primary",
            icon: <MdAdd size={20} />,
          },
        ]}
      />
      <Card type="simple">
        <Segmented
          className="dynamic-documents__kinds"
          value={kind}
          options={Object.values(DOCUMENT_KINDS).map((value) => ({
            value,
            label: t(`KINDS.${value}`),
          }))}
          onChange={setKind}
        />
        {kind === DOCUMENT_KINDS.STATIC ? (
          <StaticDocuments />
        ) : (
          dynamicContent()
        )}
      </Card>
      {creating && <NewDocumentModal onClose={() => setCreating(false)} />}
      <Modal
        visible={deleteDialog.open}
        message={LIST.DELETE.MESSAGE}
        child={
          <p className="dynamic-documents__dialog-text">
            {t("LIST.DELETE.DESCRIPTION", {
              name: get(deleteDialog, "template.name"),
            })}
          </p>
        }
        okText={LIST.DELETE.OK}
        cancelText={LIST.DELETE.CANCEL}
        onOk={confirmDelete}
        onCancel={() => setDeleteDialog(closeDialog)}
      />
      <Modal
        visible={blockedDialog.open}
        type={TYPES.INFO}
        message={LIST.DELETE.BLOCKED}
        child={
          <p className="dynamic-documents__dialog-text">
            {t("LIST.DELETE.BLOCKED_DESCRIPTION", {
              products: blockedDialog.products.join(", "),
            })}
          </p>
        }
        okText={LIST.DELETE.CLOSE}
        onOk={() => setBlockedDialog(closeDialog)}
        closable={false}
      />
    </>
  );
};

export default List;
